import React, { useState, useEffect, useCallback } from 'react';
import styles from './css/Register.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import axiosInstance from '../../apiServices/axios';
import { API_BASE_URL, REGISTER_USER, SETUP_2FA, GET_QR_CODE, SEND_SMS_CODE } from '../../apiServices/api_routes';
import { toast } from 'react-toastify';
import { isObject } from '../../utils/commonUtils';
import Logo from '../../assets/longLogoBright.png';
import { Eye16Regular, EyeOff16Regular, SpinnerIos16Regular } from '@fluentui/react-icons';
import CountryCodeSelector from '../CommonComponents/CountryCodeSelector';
import { initiateAzureRegistration } from '../../apiServices/ssoService';
import Cookies from 'js-cookie';
import { GetMyWorkspaces, GetUserData, setAccessToken, setWorkspaceSelectionRequired } from '../../redux/usersSlice';
import { takePostRegisterValidateKey } from '../../utils/pendingInviteKey';
import InviteExpiredCard from '../InviteFlow/InviteExpiredCard';
import store from '../../redux/store';

/**
 * Access token for authenticated accept-invite calls. The accept-invite effect must send JWT
 * so the API can log `invitation_email_mismatch`; Redux `user.access` can be empty on the
 * first tick while persisted state is already in localStorage.
 */
function getAccessTokenForApi() {
    const fromStore = store.getState()?.user?.access;
    if (fromStore) return fromStore;
    try {
        const raw = localStorage.getItem('persist:root');
        if (!raw) return '';
        const root = JSON.parse(raw);
        const userChunk = root.user;
        if (typeof userChunk === 'string') {
            const u = JSON.parse(userChunk);
            return u?.access || '';
        }
        if (userChunk && typeof userChunk === 'object') {
            return userChunk.access || '';
        }
    } catch (e) {
        /* ignore */
    }
    return '';
}

/** True when Redux says logged in or a JWT is already in storage (before persist rehydrates `isLoggedIn`). */
function hasAppSession(userSlice) {
    return Boolean(userSlice?.isLoggedIn || getAccessTokenForApi());
}

const Register = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(state => state.user);
    const [userData, setUserData] = useState();
    const [ip, setIP] = useState('');
    const [showExpireView, setShowExpireView] = React.useState(false);
    const [showUnableVerifyView, setShowUnableVerifyView] = React.useState(false);
    const [showpassword, setShowpassword] = useState(false);
    const [showPasswordView, setShowPasswordView] = React.useState(false);
    const [iAgree, setIAgree] = React.useState(false);
    const [showSecure, setShowSecure] = React.useState(false);
    const [twoFAMethod, setTwoFAMethod] = React.useState('qr'); // 'qr' or 'sms'
    const [smsStep, setSmsStep] = React.useState('phone'); // 'phone' or 'verification'
    const [formData, setFormData] = React.useState({
        full_name: '',
        job_title: '',
        password: '',
        organisation: '',
    });
    const [microsoftLoading, setMicrosoftLoading] = useState(false);
    const [showUserExistsMessage, setShowUserExistsMessage] = useState(false);
    const [existingUserEmail, setExistingUserEmail] = useState('');
    const [inviteLookupLoading, setInviteLookupLoading] = useState(!!params?.key);

    const routeSignedInUser = useCallback(async () => {
        try {
            const res = await dispatch(GetMyWorkspaces());
            const memberships = res?.payload?.data?.memberships || [];
            if (!memberships.length) {
                navigate('/no-workspace-access', { replace: true });
                return;
            }
            if (memberships.length === 1) {
                dispatch(setWorkspaceSelectionRequired(false));
                navigate('/project', { replace: true });
                return;
            }
            dispatch(setWorkspaceSelectionRequired(true));
            navigate('/select-workspace', { replace: true });
        } catch {
            navigate('/project', { replace: true });
        }
    }, [dispatch, navigate]);

    useEffect(() => {
        handleGetIp();
    }, []);

    // Handle error parameters from URL (SSO registration errors)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');

        if (error) {
            let errorMessage = 'Registration failed';

            switch (error) {
                case 'user_exists':
                    const email = urlParams.get('email');
                    errorMessage = `An account with email ${email} already exists. Please log in instead.`;
                    setExistingUserEmail(email);
                    setShowUserExistsMessage(true);
                    break;
                case 'email_mismatch':
                    const invitationEmail = urlParams.get('invitation_email');
                    const azureEmail = urlParams.get('azure_email');
                    errorMessage = `Email mismatch. This invitation is for ${invitationEmail}, but you signed in with ${azureEmail}. Please use the correct Microsoft account.`;
                    break;
                case 'email_missing':
                    errorMessage = 'Email not found in Microsoft account. Please try again.';
                    break;
                case 'invalid_invitation':
                    errorMessage = 'Invalid invitation link. Please contact your administrator.';
                    break;
                case 'registration_failed':
                    errorMessage = 'Registration failed. Please try again.';
                    break;
                case 'azure_token_error':
                    const reason = urlParams.get('reason');
                    errorMessage = `Microsoft authentication error: ${reason || 'Unknown error'}`;
                    break;
                default:
                    errorMessage = `Registration error: ${error}`;
            }

            toast.error(errorMessage);

            // Clear URL parameters
            navigate(`/register/${params.key}`, { replace: true });
        }
    }, [navigate, params.key]);
    const handleGetIp = async () => {
        try {
            fetch('https://geolocation-db.com/json/', {
                headers: {
                    accept: 'application/json, text/plain, */*',
                    'accept-language': 'en-US,en;q=0.9,de-DE;q=0.8,de;q=0.7',
                    'sec-ch-ua': '" Not A;Brand";v="99", "Chromium";v="101", "Google Chrome";v="101"',
                    'sec-ch-ua-mobile': '?0',
                    'sec-ch-ua-platform': '"macOS"',
                    'sec-fetch-dest': 'empty',
                    'sec-fetch-mode': 'cors',
                    'sec-fetch-site': 'cross-site',
                },
                referrerPolicy: 'strict-origin-when-cross-origin',
                body: null,
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
            })
                .then(response => response.json())
                .then(data => {
                    setIP(data?.IPv4);
                });
        } catch (error) {}
    };
    const handleChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };
    const handleSubmit = async e => {
        e.preventDefault();
        const { full_name, job_title, password, organisation } = formData;
        const missingFields = [];

        if (!full_name.trim()) {
            missingFields.push('Full Name');
        }

        if (!job_title.trim()) {
            missingFields.push('Job Title');
        }

        if (!password.trim()) {
            missingFields.push('Password');
        }

        if (!organisation.trim()) {
            missingFields.push('Organisation');
        }
        if (missingFields.length > 0) {
            missingFields?.map(t => toast.error(t + ' is required to proceed.'));
            return;
        }

        // Run register API here (without 2FA)
        try {
            let body = {};
            if (userData?.VDR) {
                body = {
                    group_type: userData?.group_type,
                    project: userData.project,
                    full_name: full_name,
                    email: userData?.email,
                    key: params?.key,
                    password: password,
                    job_title: job_title,
                    organisation: organisation,
                    vdr: userData?.VDR,
                };
            } else {
                body = {
                    email: userData?.email,
                    full_name: full_name,
                    key: params?.key,
                    password: password,
                    job_title: job_title,
                    organisation: organisation,
                };
            }

            await axios.post(`${API_BASE_URL}${REGISTER_USER}`, body);
            toast.success('Account created successfully!');

            // After successful registration, show 2FA setup (without auto-login)
            setShowSecure(true);
        } catch (error) {
            const res = error?.response?.data;
            if (isObject(res)) {
                Object.entries(res).forEach(([key, value]) => {
                    key !== 'email' &&
                        key !== 'job_title' &&
                        toast.error(
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '20px',
                                }}>
                                {<span>{value}</span>}
                            </span>,
                            {
                                position: 'bottom-right',
                                autoClose: 5000,
                                hideProgressBar: true,
                                closeOnClick: true,
                                pauseOnHover: false,
                                draggable: true,
                                progress: undefined,
                            },
                        );
                });
            }
        }
    };

    const handleMicrosoftSignup = async () => {
        try {
            setMicrosoftLoading(true);
            const response = await initiateAzureRegistration(params.key);

            if (response?.azure_auth_url) {
                // Redirect to Azure login - the backend will redirect back to complete-profile page
                window.location.href = response.azure_auth_url;
            } else {
                toast.error('Failed to get Microsoft signup URL');
                setMicrosoftLoading(false);
            }
        } catch (error) {
            console.error('Microsoft signup error:', error);
            const errorMessage = error.response?.data?.error || 'Failed to initiate Microsoft signup';
            toast.error(errorMessage);
            setMicrosoftLoading(false);
        }
    };

    // Anonymous users: load invite details for the registration flow.
    // Logged-in users: call accept-invite silently (audit only); UI never shows invite validity or invitee email.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const effectAsync = async () => {
            if (!params?.key) {
                setInviteLookupLoading(false);
                return;
            }
            if (hasAppSession(user)) {
                setUserData(undefined);
                setShowExpireView(false);
                setShowUnableVerifyView(false);
                setInviteLookupLoading(true);
                try {
                    const token = getAccessTokenForApi();
                    const resp = await axiosInstance.post(
                        `accounts/invitations/accept-invite/${params?.key}/`,
                        {},
                        {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                        },
                    );
                    const inviteEmail = (resp?.data?.email || '').trim().toLowerCase();
                    const signedInEmail = (user?.user?.email || '').trim().toLowerCase();
                    if (inviteEmail && signedInEmail && inviteEmail === signedInEmail) {
                        await routeSignedInUser();
                        return;
                    }
                } catch {
                    /* silent: same generic UI; server may still be unreachable */
                } finally {
                    setInviteLookupLoading(false);
                }
                return;
            }
            setInviteLookupLoading(true);
            setShowExpireView(false);
            setShowUnableVerifyView(false);
            try {
                const token = getAccessTokenForApi();
                const resp = await axiosInstance.post(
                    `accounts/invitations/accept-invite/${params?.key}/`,
                    {},
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    },
                );
                setUserData(resp?.data);
            } catch (error) {
                setUserData(undefined);
            } finally {
                setInviteLookupLoading(false);
            }
        };
        effectAsync();
    }, [params?.key, user, routeSignedInUser]);

    useEffect(() => {
        if (!userData || inviteLookupLoading) return;
        if (hasAppSession(user)) return;

        const msg = userData?.message || '';
        if (msg === 'No invitation exists.') {
            setShowExpireView(false);
            setShowUnableVerifyView(true);
            return;
        }

        if (typeof msg === 'string' && msg.includes('already been accepted')) {
            toast.error(msg);
            navigate('/login');
            return;
        }

        if (msg === 'This invitation has expired please request a new invitation here') {
            setShowUnableVerifyView(false);
            setShowExpireView(true);
            return;
        }
    }, [userData, inviteLookupLoading, user, navigate]);

    if (hasAppSession(user) && params?.key) {
        if (inviteLookupLoading) {
            return (
                <div className={styles.body}>
                    <div className={styles.dotedBg}>
                        <div className={styles.formCard}>
                            <div className={styles.formHeader}>
                                <img src={Logo} width={73} alt="lens-logo" />
                            </div>
                            <div className={styles.formBody} style={{ textAlign: 'center' }}>
                                <SpinnerIos16Regular className={styles.spinner} style={{ margin: '1rem auto' }} />
                                <p className={styles.formSubtitle}>Loading…</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className={styles.body}>
                <div className={styles.dotedBg}>
                    <div className={styles.formCard}>
                        <div className={styles.formHeader}>
                            <img src={Logo} width={73} alt="lens-logo" />
                        </div>
                        <div className={styles.formBody}>
                            <h5 className={styles.formLabel}>We&apos;re Unable to Verify This Invitation</h5>
                            <p className={styles.inviteGateLead}>
                                For security reasons, we&apos;re unable to validate this invitation link. This may happen if the link has been altered, was opened using a different
                                email account, or is no longer valid.
                                <br />
                                <br />
                                Please contact Lens Support for assistance.
                            </p>

                            <a
                                href="mailto:LensSupport@beyond-ma.com"
                                className={styles.submitBtn}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    textDecoration: 'none',
                                    boxSizing: 'border-box',
                                }}>
                                Contact Lens Support
                            </a>
                        </div>
                    </div>
                </div>
                <div className={styles.footer}>
                    <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                        Terms of Use
                    </a>
                    <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                        Privacy & Cookies
                    </a>
                </div>
            </div>
        );
    }

    if (showExpireView) {
        return <InviteExpiredCard invitationKey={params?.key} />;
    }

    if (showUnableVerifyView) {
        return (
            <div className={styles.body}>
                <div className={styles.dotedBg}>
                    <div className={styles.formCard}>
                        <div className={styles.formHeader}>
                            <img src={Logo} width={73} alt="lens-logo" />
                        </div>
                        <div className={styles.formBody}>
                            <h5 className={styles.formLabel}>We&apos;re Unable to Verify This Invitation</h5>
                            <p className={styles.inviteGateLead}>
                                For security reasons, we&apos;re unable to validate this invitation link. This may happen if the link has been altered, was opened using a different
                                email account, or is no longer valid.
                                <br />
                                <br />
                                Please contact Lens Support for assistance.
                            </p>

                            <a
                                href="mailto:LensSupport@beyond-ma.com"
                                className={styles.submitBtn}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    textDecoration: 'none',
                                    boxSizing: 'border-box',
                                }}>
                                Contact Lens Support
                            </a>
                        </div>
                    </div>
                </div>
                <div className={styles.footer}>
                    <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                        Terms of Use
                    </a>
                    <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                        Privacy & Cookies
                    </a>
                </div>
            </div>
        );
    }

    if (!hasAppSession(user)) {
        return (
            <div className={styles.body}>
                <div className={styles.dotedBg}>
                    <div className={styles.formCard}>
                        <div className={styles.formHeader}>
                            <img src={Logo} width={73} alt="lens-logo" />
                        </div>
                        {showSecure && (
                            <SecureAccount
                                userData={userData}
                                data={formData}
                                setShowSecure={setShowSecure}
                                twoFAMethod={twoFAMethod}
                                setTwoFAMethod={setTwoFAMethod}
                                smsStep={smsStep}
                                setSmsStep={setSmsStep}
                            />
                        )}
                        {showUserExistsMessage && (
                            <div className={styles.formBody}>
                                <h5 className={styles.formLabel}>Account Already Exists</h5>
                                <p className={styles.formSubtitle}>
                                    An account with email <strong>{existingUserEmail}</strong> already exists in our system.
                                </p>
                                <div className={styles.userExistsMessage}>
                                    <p>You can:</p>
                                    <ul>
                                        <li>Log in with your existing password</li>
                                        <li>Use "Forgot Password" if you don't remember your password</li>
                                        <li>Contact support if you need assistance</li>
                                    </ul>
                                </div>
                                <div className={styles.actionButtons}>
                                    <button onClick={() => navigate('/login')} className={styles.submitBtn}>
                                        Go to Login
                                    </button>
                                    <button onClick={() => navigate('/forgot-password')} className={styles.secondaryBtn}>
                                        Forgot Password
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowUserExistsMessage(false);
                                            setExistingUserEmail('');
                                        }}
                                        className={styles.secondaryBtnLink}>
                                        Try Different Email
                                    </button>
                                </div>
                            </div>
                        )}
                        {!showSecure && !showUserExistsMessage && showPasswordView ? (
                            <div className={styles.formBody}>
                                <h5 className={styles.formLabel}>Set Password</h5>
                                <p className={styles.formSubtitle}>Create a unique, memorable password for your account</p>
                                <div>
                                    <p className={styles.fieldLabel}>
                                        Enter Password <span style={{ color: '#B10E1C' }}>*</span>
                                    </p>
                                    <form onSubmit={handleSubmit}>
                                        <div className="input-field">
                                            <input
                                                value={formData?.password}
                                                name="password"
                                                required
                                                type={showpassword ? 'text' : 'password'}
                                                onChange={handleChange}
                                                className={'form-control ' + styles.inputField}
                                            />
                                            {showpassword ? (
                                                <Eye16Regular onClick={() => setShowpassword(!showpassword)} className="icon icon-right svg-icons" />
                                            ) : (
                                                <EyeOff16Regular onClick={() => setShowpassword(!showpassword)} className="icon icon-right svg-icons" />
                                            )}
                                        </div>{' '}
                                        <div className="d-flex align-items-center justify-content-between">
                                            <button disabled={!formData?.password} type="submit" className={styles.submitBtn}>
                                                Save
                                            </button>
                                            <span role="button" onClick={() => setShowPasswordView(false)} className={styles.secondaryBtnLink}>
                                                Back
                                            </span>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        ) : (
                            !showSecure &&
                            !showUserExistsMessage && (
                                <div className={styles.formBody}>
                                    <h5 className={styles.formLabel + ' mb-0'}>Set up account for</h5>
                                    <p className={styles.formSubtitle}>{userData?.email}</p>

                                    {/* Microsoft SSO Option */}
                                    <div className={styles.ssoSection}>
                                        <button onClick={handleMicrosoftSignup} disabled={microsoftLoading} className={styles.microsoftBtn}>
                                            {microsoftLoading ? (
                                                <SpinnerIos16Regular className={styles.spinner} />
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M10 1H1V10H10V1Z" fill="#F25022" />
                                                    <path d="M20 1H11V10H20V1Z" fill="#7FBA00" />
                                                    <path d="M10 11H1V20H10V11Z" fill="#00A4EF" />
                                                    <path d="M20 11H11V20H20V11Z" fill="#FFB900" />
                                                </svg>
                                            )}
                                            Sign up with Microsoft
                                        </button>
                                        <div className={styles.divider}>
                                            <span>or</span>
                                        </div>
                                    </div>

                                    <form
                                        onSubmit={e => {
                                            e.preventDefault();
                                            setShowPasswordView(true);
                                        }}>
                                        <div>
                                            <p className={styles.fieldLabel}>
                                                Full Name <span style={{ color: '#B10E1C' }}>*</span>
                                            </p>
                                            <input value={formData?.full_name} name="full_name" onChange={handleChange} required className={'form-control ' + styles.inputField} />
                                        </div>
                                        <div>
                                            <p className={styles.fieldLabel}>
                                                Organisation <span style={{ color: '#B10E1C' }}>*</span>
                                            </p>
                                            <input
                                                value={formData?.organisation}
                                                name="organisation"
                                                onChange={handleChange}
                                                required
                                                className={'form-control ' + styles.inputField}
                                            />
                                        </div>
                                        <div>
                                            <p className={styles.fieldLabel}>
                                                Role <span style={{ color: '#B10E1C' }}>*</span>
                                            </p>
                                            <input value={formData?.job_title} name="job_title" onChange={handleChange} required className={'form-control ' + styles.inputField} />
                                        </div>
                                        <div onClick={() => setIAgree(!iAgree)} role="button" className={styles.rememberMeSection}>
                                            <div>
                                                <input checked={iAgree} required type="checkbox" />
                                            </div>
                                            <label className={styles.rememberBeText}>
                                                I agree to the Lens{' '}
                                                <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                                                    Terms
                                                </a>{' '}
                                                and acknowledge the{' '}
                                                <a
                                                    href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no"
                                                    target="_blank"
                                                    rel="noreferrer">
                                                    Privacy Policy.
                                                </a>{' '}
                                                for information on data usage and protection.
                                            </label>
                                        </div>
                                        <div>
                                            <button className={styles.submitBtn} type="submit">
                                                Next
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )
                        )}
                    </div>
                </div>
                <div className={styles.footer}>
                    <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                        Terms of Use
                    </a>
                    <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                        Privacy & Cookies
                    </a>
                </div>
            </div>
        );
    }

    return null;
};

const SecureAccount = ({ data, setShowSecure, userData, twoFAMethod, setTwoFAMethod, smsStep, setSmsStep }) => {
    const params = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [registerLoading, setRegisterLoading] = React.useState(false);
    const [qrVerificationCode, setQrVerificationCode] = React.useState(['', '', '', '', '', '']);
    const [smsVerificationCode, setSmsVerificationCode] = React.useState(['', '', '', '', '', '']);
    const [smsData, setSmsData] = React.useState({
        code: '',
        number: '',
    });
    const [qrData, setQrData] = React.useState('');
    const [qrLoading, setQrLoading] = React.useState(false);

    // Refs for OTP inputs
    const qrInputRefs = React.useRef([]);
    const smsInputRefs = React.useRef([]);

    // Handle QR code verification input
    const handleQrCodeChange = (index, event) => {
        const newCode = [...qrVerificationCode];
        newCode[index] = event.target.value;
        setQrVerificationCode(newCode);

        // Move to the next input field
        if (event.target.value && index < 5) {
            qrInputRefs.current[index + 1].focus();
        }
    };

    // Handle QR code key down (backspace)
    const handleQrKeyDown = (index, event) => {
        // Move to the previous input field if backspace is pressed and the current input field is empty
        if (event.key === 'Backspace' && !qrVerificationCode[index] && index > 0) {
            qrInputRefs.current[index - 1].focus();
        }
    };

    // Handle QR code paste
    const handleQrPaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && qrInputRefs.current[0] === document.activeElement) {
            // If pasted data length is 6 and the first input is focused, update the OTP
            const newOTP = pastedData.split('').slice(0, 6);
            setQrVerificationCode(newOTP);
        }
    };

    // Handle SMS verification code input
    const handleSmsCodeChange = (index, event) => {
        const newCode = [...smsVerificationCode];
        newCode[index] = event.target.value;
        setSmsVerificationCode(newCode);

        // Move to the next input field
        if (event.target.value && index < 5) {
            smsInputRefs.current[index + 1].focus();
        }
    };

    // Handle SMS key down (backspace)
    const handleSmsKeyDown = (index, event) => {
        // Move to the previous input field if backspace is pressed and the current input field is empty
        if (event.key === 'Backspace' && !smsVerificationCode[index] && index > 0) {
            smsInputRefs.current[index - 1].focus();
        }
    };

    // Handle SMS paste
    const handleSmsPaste = event => {
        const pastedData = event.clipboardData.getData('Text');
        if (pastedData.length === 6 && smsInputRefs.current[0] === document.activeElement) {
            // If pasted data length is 6 and the first input is focused, update the OTP
            const newOTP = pastedData.split('').slice(0, 6);
            setSmsVerificationCode(newOTP);
        }
    };

    // Fetch QR code data
    const fetchQrCode = async () => {
        setQrLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}${GET_QR_CODE}`, {
                email: userData?.email,
            });
            setQrData(response.data.qr_data);
        } catch (error) {
            console.error('Failed to fetch QR code:', error);
            toast.error('Failed to generate QR code. Please try again.');
        } finally {
            setQrLoading(false);
        }
    };

    // Fetch QR code when QR screen is shown
    React.useEffect(() => {
        if (twoFAMethod === 'qr' && !qrData) {
            fetchQrCode();
        }
    }, [twoFAMethod]);

    // Handle SMS phone number change
    const handleSmsChange = (field, value) => {
        setSmsData(prev => ({ ...prev, [field]: value }));
    };

    // Handle QR code activation
    const handleQrActivation = async () => {
        const code = qrVerificationCode.join('');
        if (code.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }

        setRegisterLoading(true);
        try {
            // Setup 2FA with QR code
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userData?.email,
                mfa_method: 'app',
                verification_code: code,
            });

            const access = response?.data?.access;
            const refresh = response?.data?.refresh;
            if (access && refresh) {
                toast.success('2FA setup completed successfully!');
                dispatch(setAccessToken(access));
                Cookies.set('refresh', refresh, { secure: true, sameSite: 'strict' });
                await dispatch(GetUserData());
                const validateKey = takePostRegisterValidateKey();
                if (validateKey) {
                    navigate(`/validate-invite/${validateKey}`, { replace: true });
                    return;
                }
                const res = await dispatch(GetMyWorkspaces());
                const memberships = res?.payload?.data?.memberships || [];
                if (!memberships.length) {
                    navigate('/no-workspace-access', { replace: true });
                } else if (memberships.length === 1) {
                    dispatch(setWorkspaceSelectionRequired(false));
                    navigate('/project', { replace: true });
                } else {
                    dispatch(setWorkspaceSelectionRequired(true));
                    navigate('/select-workspace', { replace: true });
                }
            } else {
                toast.success('2FA setup completed successfully! Please sign in to continue.');
                takePostRegisterValidateKey();
                navigate(params?.key ? `/login?invite=${encodeURIComponent(params.key)}` : '/login');
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Invalid verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setRegisterLoading(false);
        }
    };

    // Handle SMS phone number submission
    const handleSendSmsCode = async () => {
        if (!smsData.code || !smsData.number) {
            toast.error('Please enter your phone number');
            return;
        }

        setRegisterLoading(true);
        try {
            // Send SMS verification code
            const phoneNumber = smsData.code + smsData.number;
            await axios.post(`${API_BASE_URL}${SEND_SMS_CODE}`, {
                email: userData?.email,
                phone_number: phoneNumber,
            });

            toast.success('Verification code sent to your phone!');
            setSmsStep('verification');
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Failed to send verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setRegisterLoading(false);
        }
    };

    // Handle SMS verification code submission
    const handleSmsActivation = async () => {
        const code = smsVerificationCode.join('');
        if (code.length !== 6) {
            toast.error('Please enter a 6-digit verification code');
            return;
        }

        setRegisterLoading(true);
        try {
            // Setup 2FA with SMS
            const phoneNumber = smsData.code + smsData.number;
            const response = await axios.post(`${API_BASE_URL}${SETUP_2FA}`, {
                email: userData?.email,
                mfa_method: 'sms_twilio',
                phone_number: phoneNumber,
                verification_code: code,
            });

            const access = response?.data?.access;
            const refresh = response?.data?.refresh;
            if (access && refresh) {
                toast.success('2FA setup completed successfully!');
                dispatch(setAccessToken(access));
                Cookies.set('refresh', refresh, { secure: true, sameSite: 'strict' });
                await dispatch(GetUserData());
                const validateKey = takePostRegisterValidateKey();
                if (validateKey) {
                    navigate(`/validate-invite/${validateKey}`, { replace: true });
                    return;
                }
                const res = await dispatch(GetMyWorkspaces());
                const memberships = res?.payload?.data?.memberships || [];
                if (!memberships.length) {
                    navigate('/no-workspace-access', { replace: true });
                } else if (memberships.length === 1) {
                    dispatch(setWorkspaceSelectionRequired(false));
                    navigate('/project', { replace: true });
                } else {
                    dispatch(setWorkspaceSelectionRequired(true));
                    navigate('/select-workspace', { replace: true });
                }
            } else {
                toast.success('2FA setup completed successfully! Please sign in to continue.');
                takePostRegisterValidateKey();
                navigate(params?.key ? `/login?invite=${encodeURIComponent(params.key)}` : '/login');
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Invalid verification code. Please try again.';
            toast.error(errorMessage);
        } finally {
            setRegisterLoading(false);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        if (twoFAMethod === 'sms' && smsStep === 'verification') {
            setSmsStep('phone');
        } else if (twoFAMethod === 'sms' && smsStep === 'phone') {
            setTwoFAMethod('qr');
        } else {
            setShowSecure(false);
        }
    };
    console.log({ smsData });
    // QR Code Screen
    if (twoFAMethod === 'qr') {
        return (
            <div className={styles.formBody}>
                <h5 className={styles.formLabel}>Set up 2FA</h5>
                <p className={styles.formSubtitle}>
                    For heightened security measures, kindly scan the provided code using your authenticator app and enter the corresponding verification code below. We will use
                    this method by default.
                </p>

                <div className="text-center">
                    <div className="d-flex justify-content-center mb-3">
                        {qrLoading ? (
                            <div
                                style={{
                                    width: '168px',
                                    height: '168px',
                                    backgroundColor: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                }}>
                                <SpinnerIos16Regular className="rotateLoader" />
                            </div>
                        ) : qrData ? (
                            <div
                                style={{
                                    width: '168px',
                                    height: '168px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                    padding: '8px',
                                }}>
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`}
                                    alt="QR Code"
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: '168px',
                                    height: '168px',
                                    backgroundColor: '#f8f9fa',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                }}>
                                <span className="text-muted">QR Code</span>
                            </div>
                        )}
                    </div>
                    <p className="text-muted small">Open your authenticator app and scan this QR code to add your Lens account.</p>
                </div>

                <div className="mb-3">
                    <label className={styles.fieldLabel}>Enter verification code</label>
                    <div className="d-flex justify-content-center">
                        <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                            {[0, 1, 2, 3, 4, 5].map(index => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={qrVerificationCode[index]}
                                    onChange={event => handleQrCodeChange(index, event)}
                                    onKeyDown={event => handleQrKeyDown(index, event)}
                                    ref={ref => (qrInputRefs.current[index] = ref)}
                                    onPaste={event => handleQrPaste(event)}
                                    className={'form-control text-center ' + styles.inputField}
                                    style={{
                                        width: '14%',
                                        height: '48px',
                                        fontSize: '18px',
                                        fontFamily: 'monospace',
                                        fontWeight: 'bold',
                                        border: 'none',
                                        borderBottom: '1px solid var(--Neutral-Gray-6)',
                                        outline: 'none',
                                        textAlign: 'center',
                                        borderRadius: '0px',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleQrActivation} disabled={registerLoading || qrVerificationCode.join('').length !== 6} className={styles.submitBtn}>
                        Activate account {registerLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <span role="button" onClick={() => setTwoFAMethod('sms')} className={styles.secondaryBtnLink}>
                        Use SMS authentication
                    </span>
                </div>
            </div>
        );
    }

    // SMS Phone Number Screen
    if (twoFAMethod === 'sms' && smsStep === 'phone') {
        return (
            <div className={styles.formBody}>
                <h5 className={styles.formLabel}>Set up 2FA</h5>
                <p className={styles.formSubtitle}>
                    For heightened security measures, add your mobile number below to receive verification code. We will use this method by default. Your mobile number will only be
                    used for account security. Standard SMS charges will apply.
                </p>

                <div className="d-flex">
                    <div style={{ width: '120px' }} className="me-2">
                        <div>
                            <p className={styles.fieldLabel}>Code</p>
                            <CountryCodeSelector
                                value={smsData?.code}
                                onChange={value => handleSmsChange('code', value)}
                                placeholder="Please Select"
                                className="" // Remove problematic CSS class
                                name="code"
                                ariaLabel="Country code"
                            />
                        </div>
                    </div>
                    <div className="w-100">
                        <div>
                            <p className={styles.fieldLabel}>Mobile Number</p>
                            <input
                                type="number"
                                placeholder="Mobile Number"
                                value={smsData?.number}
                                onChange={e => handleSmsChange('number', e.target.value)}
                                className={'form-control ' + styles.inputField}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleSendSmsCode} disabled={!smsData?.code || !smsData?.number || registerLoading} className={styles.submitBtn}>
                        Send Code {registerLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                        Back
                    </span>
                </div>
            </div>
        );
    }

    // SMS Verification Code Screen
    if (twoFAMethod === 'sms' && smsStep === 'verification') {
        return (
            <div className={styles.formBody}>
                <h5 className={styles.formLabel}>Enter verification code</h5>
                <p className={styles.formSubtitle}>The verification code is sent on your mobile number</p>

                <div className="mb-3">
                    <label className={styles.fieldLabel}>Enter verification code</label>
                    <div className="d-flex justify-content-center">
                        <div className="d-flex" style={{ gap: '8px', justifyContent: 'space-between', width: '100%' }}>
                            {[0, 1, 2, 3, 4, 5].map(index => (
                                <input
                                    key={index}
                                    type="text"
                                    maxLength="1"
                                    value={smsVerificationCode[index]}
                                    onChange={event => handleSmsCodeChange(index, event)}
                                    onKeyDown={event => handleSmsKeyDown(index, event)}
                                    ref={ref => (smsInputRefs.current[index] = ref)}
                                    onPaste={event => handleSmsPaste(event)}
                                    className={'form-control text-center ' + styles.inputField}
                                    style={{
                                        width: '14%',
                                        height: '48px',
                                        fontSize: '18px',
                                        fontFamily: 'monospace',
                                        fontWeight: 'bold',
                                        border: 'none',
                                        borderBottom: '1px solid var(--Neutral-Gray-6)',
                                        outline: 'none',
                                        textAlign: 'center',
                                        borderRadius: '0px',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-4">
                    <button onClick={handleSmsActivation} disabled={smsVerificationCode.join('').length !== 6 || registerLoading} className={styles.submitBtn}>
                        Activate account {registerLoading && <SpinnerIos16Regular className="rotateLoader ms-1" />}
                    </button>
                    <div className="d-flex flex-column align-items-end">
                        <span role="button" onClick={handleBack} className={styles.secondaryBtnLink}>
                            Back
                        </span>
                        <span role="button" onClick={handleSendSmsCode} className={styles.secondaryBtnLink} style={{ fontSize: '14px', marginTop: '8px' }}>
                            Resend code
                        </span>
                    </div>
                </div>
            </div>
        );
    }
};

export default Register;
